import { forwardRef } from 'react';
import { Select as AntSelect } from 'antd';
import type { RefSelectProps, SelectProps } from 'antd';
import type { BaseOptionType, DefaultOptionType } from 'rc-select/lib/Select';
import { TbChevronDown, TbDatabase } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import EmptyView from '@/shared/components/layouts/views/EmptySection';

const PlatformSelectBase = forwardRef<RefSelectProps, SelectProps<unknown, BaseOptionType | DefaultOptionType>>(
    ({ notFoundContent, suffixIcon, ...props }, ref) => {
        const { token } = useAntDesignTheme();

        return (
            <AntSelect
                {...props}
                ref={ref}
                notFoundContent={
                    notFoundContent === undefined ? (
                        <EmptyView
                            description={
                                props.mode === 'tags'
                                    ? 'Type a value and press Enter to add it.'
                                    : 'There are no values available to choose from yet.'
                            }
                            height='9rem'
                            icon={<TbDatabase size='1.25rem' />}
                            label={props.mode === 'tags' ? 'Add a Value' : 'Nothing To Select'}
                        />
                    ) : (
                        notFoundContent
                    )
                }
                suffixIcon={
                    suffixIcon === undefined ? (
                        <TbChevronDown
                            aria-hidden='true'
                            focusable='false'
                            size='1rem'
                            style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                        />
                    ) : (
                        suffixIcon
                    )
                }
            />
        );
    },
);

PlatformSelectBase.displayName = 'PlatformSelect';

/**
 * The platform-standard Ant Design Select.
 *
 * This preserves Ant Select's generic and static API while providing the
 * shared Tabler chevron by default. Passing `suffixIcon` (including `null`)
 * continues to opt an individual Select into a specialized treatment.
 */
const PlatformSelect = Object.assign(PlatformSelectBase, {
    _InternalPanelDoNotUseOrYouWillBeFired: AntSelect._InternalPanelDoNotUseOrYouWillBeFired,
    OptGroup: AntSelect.OptGroup,
    Option: AntSelect.Option,
    SECRET_COMBOBOX_MODE_DO_NOT_USE: AntSelect.SECRET_COMBOBOX_MODE_DO_NOT_USE,
}) as typeof AntSelect;

export type PlatformSelectProps<
    ValueType = unknown,
    OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> = SelectProps<ValueType, OptionType>;

export default PlatformSelect;
